import AddressBookOverlay from "@insite/client-framework/Components/AddressBookOverlay";
import BypassedAuthorizationWarning from "@insite/client-framework/Components/BypassedAuthorizationWarning";
import CompactHeader from "@insite/client-framework/Components/CompactHeader";
import {
    createPageElement,
    registerPageUpdate,
    unregisterPageUpdate,
} from "@insite/client-framework/Components/ContentItemStore";
import DelayedSpinner from "@insite/client-framework/Components/DelayedSpinner";
import ErrorModal from "@insite/client-framework/Components/ErrorModal";
import Footer from "@insite/client-framework/Components/Footer";
import Header from "@insite/client-framework/Components/Header";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { sendToShell } from "@insite/client-framework/Components/ShellHole";
import SignInOverlay from "@insite/client-framework/Components/SignInOverlay";
import StorePickupOverlay from "@insite/client-framework/Components/StorePickupOverlay";
import { getDisplayErrorPage, getErrorStatusCode, redirectTo } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { setFavicon } from "@insite/client-framework/Store/CommonHandlers/SetFavicon";
import { setMetadata } from "@insite/client-framework/Store/CommonHandlers/SetMetadata";
import { setOdpTracking } from "@insite/client-framework/Store/CommonHandlers/SetOdpTracking";
import { setStripeScript } from "@insite/client-framework/Store/CommonHandlers/SetStripeScript";
import { setTracking } from "@insite/client-framework/Store/CommonHandlers/SetTracking";
import prepareHome from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/PrepareHome";
import prepareOther from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/PrepareOther";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
// eslint-disable-next-line spire/fenced-imports
import PageLayout from "@insite/content-library/PageLayout";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const page = getCurrentPage(state);
    return {
        page,
        websiteName: state.context.website.name,
        wasSetMetadataCalled: state.context.wasSetMetadataCalled,
        errorPageLink: getPageLinkByPageType(state, "UnhandledErrorPage"),
        permissionsLoaded: !!state.context.permissions,
        enableServerSideRendering: getSettingsCollection(state).websiteSettings.enableServerSideRendering,
    };
};

const mapDispatchToProps = {
    setMetadata,
    setTracking,
    setOdpTracking,
    setStripeScript,
    setFavicon,
    prepareHome,
    prepareOther,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    ThemeProps<BaseTheme>;

class PublicPage extends React.Component<Props> {
    componentDidMount() {
        const { page, enableServerSideRendering } = this.props;
        if (module.hot) {
            this.forceUpdate = this.forceUpdate.bind(this);
            registerPageUpdate(this.forceUpdate);
        }

        sendToShell({
            type: "LoadPageComplete",
            pageId: page.id,
            parentId: page.parentId,
            layoutPageId: page.layoutPageId,
        });

        if (enableServerSideRendering || page.type === "NotFoundErrorPage") {
            this.prepareTrack();
        }
    }

    UNSAFE_componentWillMount() {
        const { setStripeScript, wasSetMetadataCalled, setMetadata, setTracking, setOdpTracking, setFavicon } =
            this.props;
        if (!IS_SERVER_SIDE) {
            setStripeScript();
        }
        if (!wasSetMetadataCalled) {
            setMetadata();
            setTracking();
            setOdpTracking();
            setFavicon();
        }
    }

    componentWillUnmount() {
        if (module.hot) {
            unregisterPageUpdate(this.forceUpdate);
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { page, setMetadata } = this.props;
        if (page.id !== prevProps.page?.id) {
            setMetadata();
            this.prepareTrack();
        }
    }

    prepareTrack() {
        const { page, prepareHome, prepareOther } = this.props;
        if (page.type === "HomePage") {
            prepareHome();
        } else if (page.type !== "NullPage") {
            prepareOther({ url: window.location.pathname });
        }
    }

    wrapContent(content: ReturnType<typeof createPageElement>) {
        const {
            page: {
                type,
                fields: { hideHeader, hideBreadcrumbs, hideFooter },
            },
            theme: {
                header: { isCompactHeader },
                signIn,
                storePickup,
                addressBook,
            },
        } = this.props;

        switch (type) {
            case "Header":
            case "Footer":
            case "CompactHeader":
            case "SignInOverlay":
            case "StorePickupOverlay":
            case "AddressBookOverlay":
            case "FlyoutNavigation":
            case "SharedContent":
            case "MaintenancePage":
            case "VariantRootPage":
                return <>{content}</>;
        }

        const pageContent = (
            <>
                {content}
                <ErrorModal />
                <BypassedAuthorizationWarning />
            </>
        );

        const result = type.startsWith("Mobile/") ? (
            pageContent
        ) : (
            <PageLayout
                showHeader={!hideHeader}
                header={<Header />}
                showCompactHeader={isCompactHeader && !hideHeader}
                compactHeader={<CompactHeader />}
                pageContent={pageContent}
                showBreadcrumbs={!hideBreadcrumbs}
                showFooter={!hideFooter}
                footer={<Footer />}
                showSignInOverlay={signIn?.useOverlay}
                signInOverlay={<SignInOverlay />}
                showStorePickupOverlay={storePickup?.useOverlay}
                storePickupOverlay={<StorePickupOverlay />}
                showAddressBookOverlay={addressBook?.useOverlay}
                addressBookOverlay={<AddressBookOverlay />}
            />
        );
        return <div data-test-selector={`page_${type}`}>{result}</div>;
    }

    render() {
        const {
            errorPageLink,
            page,
            permissionsLoaded,
            shellContext: { isInShell },
        } = this.props;
        if (getDisplayErrorPage()) {
            const errorCode = getErrorStatusCode();
            if (errorCode === 403 || errorCode === 404) {
                page.type !== "NotFoundErrorPage" && redirectTo("/NotFoundErrorPage");
            } else if (errorPageLink) {
                page.type !== errorPageLink.type && redirectTo(errorPageLink.url);
            }
        }

        if (isInShell && page.type.startsWith("Mobile/") && !permissionsLoaded) {
            return null;
        }

        if (page.id === "") {
            return this.wrapContent(<DelayedSpinner isWidget={false} />);
        }

        return this.wrapContent(createPageElement(page.type, page));
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(PublicPage)));
